import React, { useState, useContext, useEffect } from "react";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import CloseIcon from "assets/icons/close.svg?react";
import ModalsManagerContext from "contexts/ModalsManager";
import PackagesContext from "contexts/PackagesContext";
import WorkspaceContext from "contexts/CurrentWorkspace";
import { ITask } from "dto/ITask";
import taskService, { IUpdateTaskPayload } from "api/taskService";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import EventForm from "./EventForm";
import TaskForm from "./TaskForm";
import Button from "components/Button";
import moment from "moment";
import { IWorkspaceMember } from "dto/IWorkspace";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import { getPrivilages } from "utils/getPrivilages";
import { ITag } from "dto/ITag";
import ActionsContext from "contexts/ActionsContext";
import { ICalendar } from "dto/IBusyDate";
import { useTranslation } from "react-i18next";

const CreateTaskModal: FC<Props> = ({ type, initialValues, onClose, pack }) => {
  const { t } = useTranslation();
  const [packToInsert, setPackToInsert] = useState(pack ?? null);
  const { mutate: createTask, isLoading: isCreateTask } =
    taskService.useUpdateTask();
  const { mutate: updateTask, isLoading: isUpdateTask } =
    taskService.useUpdateEvent();

  const { modals, setModals } = useContext(ModalsManagerContext);
  const { packages, setPackages } = useContext(PackagesContext);
  const { tasks, setTasks } = useContext(ActionsContext);
  const { workspace } = useContext(WorkspaceContext);

  const [c, setCase] = useState<ICaseSimple | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [title, setTitle] = useState(initialValues?.title ?? "");
  const [content, setContent] = useState(initialValues?.content ?? "");
  const [tags, setTags] = useState<ITag[]>(initialValues?.tags ?? []);
  const [members, setMembers] = useState<IWorkspaceMember[]>([]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [isCalendarEvent, setIsCalendarEvent] = useState<boolean>(
    type === "event",
  );

  const [calendar, setCalendar] = useState<ICalendar>();
  const myPrivilages = getPrivilages(workspace?.Roles?.[0]);
  const [allDay, setAllDay] = useState(false);

  const closeModal = (task?: ITask) => {
    if (onClose && task) {
      onClose(task);
      return;
    }
    setModals({ ...modals, hide: true });
  };

  useEffect(() => {
    const item = localStorage.getItem("action");
    const actionType = localStorage.getItem("actionType");

    if (actionType === "EVENT") {
      setIsCalendarEvent(true);
    }

    if (item) {
      const parsedItem = JSON.parse(item);
      console.log("Edit task", parsedItem);
      setTitle(parsedItem.title ?? parsedItem.Title);
      setTaskId(parsedItem.id ?? parsedItem.Id);
      setStartDate(parsedItem.start ?? parsedItem.FromDate);
      setContent(parsedItem.description ?? parsedItem.Note);
      setCase(parsedItem.case ?? parsedItem.Case);
      if (!parsedItem.allDay && !parsedItem.AllDay) {
        setAllDay(false);
        setEndDate(parsedItem.end ?? parsedItem.ToDate);
      } else {
        setAllDay(true);
      }
    }
    localStorage.removeItem("action");
    localStorage.removeItem("actionType");
  }, []);

  useEffect(() => {
    const item = localStorage.getItem("add_to_calendar");
    if (!item) return;

    const info = JSON.parse(item);

    setTitle(info.title);
    setContent(info.description);
    setAllDay(false);
    setStartDate(moment(info.start).toDate());
    setEndDate(moment(info.end).toDate());
    localStorage.removeItem("add_to_calendar");
  }, []);

  useEffect(() => {
    const p = localStorage.getItem("options_package");
    const d = localStorage.getItem("createOrEditDate");
    const allDay = localStorage.getItem("allDay");
    if (allDay) {
      setAllDay(JSON.parse(allDay));
    }
    if (d) {
      const transformedDate = JSON.parse(d);
      setStartDate(transformedDate[0]);
      setEndDate(transformedDate[1]);
    }

    if (p) {
      const transformedPack = JSON.parse(p);
      setPackToInsert(transformedPack);

      console.log("Associated pack", transformedPack);

      if (transformedPack.Case) {
        setCase(transformedPack.Case);
      }
    }
    localStorage.removeItem("options_package");
    localStorage.removeItem("createOrEditDate");
    localStorage.removeItem("allDay");
  }, []);

  const handleCreateTask = () => {
    const payload: IUpdateTaskPayload = {
      Id: taskId ? taskId : null,
      OwnerId: packToInsert ? packToInsert.Id : c?.Id ?? "",
      OwnerKind: packToInsert ? packToInsert.Kind : c?.Kind ?? 0,
      Allday: allDay,
      FromDate: startDate ? moment(startDate).toISOString(true) : null,
      ToDate: endDate ? moment(endDate).toISOString(true) : null,

      IsFavourite: true,
      Title: title,
      Note: content,
      TagIds: tags.map((tag) => tag.Id),

      isTask: !isCalendarEvent,
      ForUserId: members.at(0) ? members.at(0)?.UserId : undefined,
      WorkspaceId: members.at(0) ? getWorkspaceId() ?? "" : undefined,
      CalendarId: calendar?.id,
    };

    if (taskId) {
      updateTask(payload, {
        onSuccess: (response) => {
          const newTasks = [...tasks].filter(
            (task) =>
              task.Id !== response.data.Id && task.id !== response.data.Id,
          );

          if (isCalendarEvent) {
            response.data.type = 0;
          } else {
            response.data.type = 1;
          }
          setTasks([response.data, ...newTasks]);
          closeModal();
        },
      });

      return;
    }

    createTask(payload, {
      onSuccess: (response) => {
        if (packToInsert) {
          const newPacks = [...packages];
          const p = newPacks.find((a) => a.Id === packToInsert.Id);
          if (p) {
            p.HasTasks = true;
            setPackages(newPacks);
          }
        }
        if (isCalendarEvent) {
          response.data.type = 0;
        } else {
          response.data.type = 1;
        }

        setTasks([response.data, ...tasks]);
        closeModal();
      },
    });
  };

  return (
    <ModalLayout
      dock={DockPosition.DISABLED}
      off={true}
      customX={8}
      customY={0}
      ignore
      isAnimation
      fullHeight={true}
      name="createTask"
    >
      <div className={styles.wrapper}>
        <div className={styles.title}>
          {type === "event" ? t("modals.newEvent") : t("modals.newTask")}
          <CloseIcon
            onClick={() => {
              closeModal();
            }}
            className={styles.closeIcon}
          />
        </div>
        <div className={styles.content}>
          {isCalendarEvent ? (
            <EventForm
              c={c}
              setCase={setCase}
              title={title}
              setTitle={setTitle}
              content={content}
              setContent={setContent}
              tags={tags}
              setTags={setTags}
              members={members}
              setMembers={setMembers}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              setAllDay={setAllDay}
              allDay={allDay}
              calendar={calendar}
              setCalendar={setCalendar}
            />
          ) : (
            <TaskForm
              c={c}
              setCase={setCase}
              content={content}
              setContent={setContent}
              members={members}
              setMembers={setMembers}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              myPrivilages={myPrivilages}
              tags={tags}
              setTags={setTags}
              title={title}
              setTitle={setTitle}
              setAllDay={setAllDay}
              allDay={allDay}
              calendar={calendar}
              setCalendar={setCalendar}
            />
          )}
        </div>
        <div className={styles.footer}>
          <div className={styles.buttonWrapper}>
            <Button
              loading={isCreateTask || isUpdateTask}
              onClick={handleCreateTask}
              text={initialValues ? t("common.edit") : t("common.create")}
            />
          </div>
        </div>
      </div>
    </ModalLayout>
  );
};

export default CreateTaskModal;
